/**
 * Application chrome: top bar, side navigation and status bar.
 *
 * Geometry comes from the design system (52px top bar, 248px side nav, 28px
 * status bar); this file only fills those regions with the app's own content.
 */
import type { ReactNode } from "react";

import type { Health } from "../api";
import { Icon, IconButton } from "./primitives";

/** The screens reachable from the side navigation. */
export type ScreenId =
  | "documents"
  | "compare"
  | "assistant"
  | "howto"
  | "contact"
  | "feedback";

export interface NavItem {
  id: ScreenId;
  label: string;
  icon: string;
  badge?: number;
}

/**
 * Where "New drawing" used to be there is now a note instead of a button.
 *
 * Removing the control silently would read as a missing feature; saying what the
 * demo is turns the same fact into an answer. The wording names what this build
 * does - a fixed set of drawings - and where uploading lives, so nobody has to
 * ask whether it is broken or coming.
 */
export function TopBar({
  onToggleAssistant,
  assistantOpen,
  onToggleNav,
  navOpen,
}: {
  onToggleAssistant: () => void;
  assistantOpen: boolean;
  /** Opens the side navigation as a drawer - only shown on phones, where it is hidden. */
  onToggleNav: () => void;
  navOpen: boolean;
}) {
  return (
    <header className="dac-topbar" style={{ position: "relative" }}>
      <IconButton
        icon={navOpen ? "close" : "menu"}
        label={navOpen ? "Close the menu" : "Open the menu"}
        onClick={onToggleNav}
        aria-expanded={navOpen}
        className="topbar__menu"
      />
      <div className="dac-topbar__brand">
        <img className="dac-topbar__logo" src="/logo-dac-digital-white.png" alt="DAC.digital" style={{ height: 26 }} />
      </div>
      <span className="dac-topbar__divider topbar__title" />
      <span className="topbar__title" style={{ fontSize: "var(--text-ui-size)", fontWeight: 600 }}>
        Technical Drawings Reader Demo
      </span>
      <span className="dac-topbar__spacer" />
      {/*
        The note that used to sit here said what the band above the app now says,
        in the same words. Two of them read as two different limitations.
      */}
      <IconButton
        icon="forum"
        label="Assistant panel"
        onClick={onToggleAssistant}
        active={assistantOpen}
      />
    </header>
  );
}

export function SideNav({
  items,
  about,
  value,
  onChange,
  footer,
  open = false,
}: {
  /** Drawer state on phones; ignored where the navigation is always on screen. */
  open?: boolean;
  items: NavItem[];
  /**
   * The pages about the demo rather than in it - the walkthrough and the contact
   * form. Their own section, because a menu that mixes "Compare" with "Contact
   * us" makes the reader check every entry to see which kind it is.
   */
  about: NavItem[];
  value: ScreenId;
  onChange: (id: ScreenId) => void;
  footer?: ReactNode;
}) {
  const entry = (item: NavItem) => (
    <button
      key={item.id}
      className={`dac-navitem ${value === item.id ? "dac-navitem--on" : ""}`.trim()}
      onClick={() => onChange(item.id)}
      aria-current={value === item.id ? "page" : undefined}
      // On a tablet the menu is an icon strip, and the icon is all that is left
      // of the entry - the label goes in the tooltip.
      title={item.label}
    >
      <Icon name={item.icon} className="dac-navitem__icon" />
      <span className="dac-navitem__label">{item.label}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <span className="dac-navitem__badge">{item.badge}</span>
      )}
    </button>
  );

  return (
    <nav className={`dac-sidenav ${open ? "sidenav--open" : ""}`.trim()}>
      <div className="dac-sidenav__section">
        <span>Workspace</span>
      </div>
      <div className="dac-sidenav__list">{items.map(entry)}</div>
      <div className="dac-sidenav__section">
        <span>About this demo</span>
      </div>
      <div className="dac-sidenav__list">{about.map(entry)}</div>
      {footer && <div className="dac-sidenav__foot">{footer}</div>}
    </nav>
  );
}

export function StatusBar({ health }: { health: Health | null }) {
  const connected = health?.ok ?? false;
  return (
    <footer className="dac-statusbar">
      <span className="dac-statusbar__item" title={health?.detail ?? "Checking the library..."}>
        <span
          className="dac-statusbar__dot"
          style={{ background: connected ? "var(--status-success)" : "var(--status-danger)" }}
        />
        {health === null ? "Connecting..." : connected ? "Connected" : "Library unavailable"}
      </span>
      {health && (
        <span className="dac-statusbar__item" title={health.library || "The document library"}>
          {health.documents} documents
        </span>
      )}
      <span className="dac-statusbar__spacer" />
      {/*
        Says what the visitor cannot do, not what the store is. "Read-only
        library" is a property of the backend; "you cannot use your own drawings"
        is the thing somebody at the end of a demo actually wants to know.
      */}
      <span className="dac-statusbar__item u-muted statusbar__note">
        Limited demo - your own drawings cannot be uploaded
      </span>
    </footer>
  );
}
